/**
 * SEO : le titre et la description de chaque page éditable, au même endroit.
 *
 * L'écran existe parce que ces deux champs ne se jugent pas page par page mais
 * les uns par rapport aux autres — deux titres identiques se cannibalisent, et
 * un titre manquant ne se remarque que dans une liste. D'où le tableau : il dit
 * d'un coup d'œil ce qui manque, ce qui déborde, et ce qui fait doublon.
 *
 * Les pages statiques (accueil, devis, contact, mentions légales…) n'y figurent
 * pas : leur titre est écrit dans les gabarits Astro, pas en base. Les afficher
 * ici laisserait croire qu'on peut les modifier.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { ErreurApi } from "../api/client";
import { PiedFormulaire, erreurChamp } from "../composants/ContenuAtelier";
import {
  type Commune,
  type NomRessource,
  type Prestation,
  type Realisation,
  useCommunes,
  useEnregistrer,
  usePrestations,
  useRealisations,
} from "../composants/ContenuDonnees";
import { ChampsSeo, etatDescription, etatTitre } from "../composants/ContenuSeo";

type PageSeo = {
  ressource: Extract<NomRessource, "prestations" | "communes" | "realisations">;
  id: number;
  titre: string;
  chemin: string;
  publiee: boolean;
  metaTitle: string;
  metaDescription: string;
};

const LIBELLES_RESSOURCE: Record<PageSeo["ressource"], string> = {
  prestations: "Prestation",
  communes: "Commune",
  realisations: "Réalisation",
};

export function Seo() {
  const prestations = usePrestations();
  const communes = useCommunes();
  const realisations = useRealisations();

  // Une mutation par ressource : les hooks ne se choisissent pas à l'exécution,
  // et un `useEnregistrer` monté conditionnellement casserait l'ordre des hooks.
  const majPrestation = useEnregistrer<Prestation>("prestations");
  const majCommune = useEnregistrer<Commune>("communes");
  const majRealisation = useEnregistrer<Realisation>("realisations");

  const [selection, setSelection] = useState<PageSeo | null>(null);
  const [brouillon, setBrouillon] = useState({ metaTitle: "", metaDescription: "" });
  const [succes, setSucces] = useState(false);
  const [filtre, setFiltre] = useState<PageSeo["ressource"] | "toutes">("toutes");

  const pages: PageSeo[] = [
    ...(prestations.data ?? []).map((prestation) => ({
      ressource: "prestations" as const,
      id: prestation.id,
      titre: prestation.nom,
      chemin: `/${prestation.slug}/`,
      publiee: prestation.isPublished,
      metaTitle: prestation.metaTitle,
      metaDescription: prestation.metaDescription,
    })),
    ...(communes.data ?? []).map((commune) => ({
      ressource: "communes" as const,
      id: commune.id,
      titre: commune.nom,
      chemin: `/renovation-interieure-${commune.slug}/`,
      publiee: commune.isPublished,
      metaTitle: commune.metaTitle,
      metaDescription: commune.metaDescription,
    })),
    ...(realisations.data ?? []).map((realisation) => ({
      ressource: "realisations" as const,
      id: realisation.id,
      titre: realisation.titre,
      chemin: `/realisations/${realisation.slug}/`,
      publiee: realisation.isPublished,
      metaTitle: realisation.metaTitle,
      metaDescription: realisation.metaDescription,
    })),
  ];

  const visibles = filtre === "toutes" ? pages : pages.filter((page) => page.ressource === filtre);

  // Doublons de titre : deux pages qui portent le même titre se disputent la
  // même requête, et le moteur en écarte une.
  const occurrences = new Map<string, number>();
  for (const page of pages) {
    const titre = page.metaTitle.trim().toLowerCase();
    if (titre === "") continue;
    occurrences.set(titre, (occurrences.get(titre) ?? 0) + 1);
  }
  const duplique = (page: PageSeo) =>
    (occurrences.get(page.metaTitle.trim().toLowerCase()) ?? 0) > 1;

  const chargement = prestations.isPending || communes.isPending || realisations.isPending;
  const mutation =
    selection === null
      ? null
      : selection.ressource === "prestations"
        ? majPrestation
        : selection.ressource === "communes"
          ? majCommune
          : majRealisation;

  const reference =
    selection === null
      ? { metaTitle: "", metaDescription: "" }
      : { metaTitle: selection.metaTitle, metaDescription: selection.metaDescription };

  const modifie =
    brouillon.metaTitle !== reference.metaTitle ||
    brouillon.metaDescription !== reference.metaDescription;

  function ouvrir(page: PageSeo) {
    majPrestation.reset();
    majCommune.reset();
    majRealisation.reset();
    setSelection(page);
    setBrouillon({ metaTitle: page.metaTitle, metaDescription: page.metaDescription });
    setSucces(false);
  }

  function enregistrer() {
    if (selection === null || mutation === null) return;

    const champs: Record<string, string> = {};
    if (brouillon.metaTitle !== reference.metaTitle) champs.metaTitle = brouillon.metaTitle;
    if (brouillon.metaDescription !== reference.metaDescription) {
      champs.metaDescription = brouillon.metaDescription;
    }
    if (Object.keys(champs).length === 0) return;

    mutation.mutate(
      { id: selection.id, champs },
      {
        onSuccess: () => {
          // La liste sera rafraîchie par l'invalidation ; la sélection reflète
          // dès maintenant ce qui vient d'être accepté.
          setSelection({ ...selection, ...brouillon });
          setSucces(true);
        },
      },
    );
  }

  const erreur: ErreurApi | null = mutation?.error ?? null;
  const sansTitre = pages.filter((page) => page.metaTitle.trim() === "");
  const sansDescription = pages.filter((page) => page.metaDescription.trim() === "");

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Titre et description de chaque page éditable. Ces deux valeurs sont recopiées telles quelles
        dans la page publiée, et alimentent aussi OpenGraph et Twitter.
      </Typography>

      {sansTitre.length + sansDescription.length > 0 && (
        <Alert severity="error">
          {sansTitre.length > 0 && (
            <>
              {sansTitre.length} page{sansTitre.length > 1 ? "s" : ""} sans titre :{" "}
              {sansTitre.map((page) => page.titre).join(", ")}.{" "}
            </>
          )}
          {sansDescription.length > 0 && (
            <>
              {sansDescription.length} page{sansDescription.length > 1 ? "s" : ""} sans description
              : {sansDescription.map((page) => page.titre).join(", ")}.
            </>
          )}
        </Alert>
      )}

      <Alert severity="info">
        Les pages statiques — accueil, devis, zone d'intervention, à propos, contact, mentions
        légales — tiennent leur titre de leurs gabarits Astro et ne se modifient pas ici.
      </Alert>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={filtre}
        onChange={(_evenement, valeur) => {
          if (valeur !== null) setFiltre(valeur as PageSeo["ressource"] | "toutes");
        }}
        aria-label="Filtrer par type de page"
      >
        <ToggleButton value="toutes">Toutes</ToggleButton>
        <ToggleButton value="prestations">Prestations</ToggleButton>
        <ToggleButton value="communes">Communes</ToggleButton>
        <ToggleButton value="realisations">Réalisations</ToggleButton>
      </ToggleButtonGroup>

      {chargement ? (
        <Skeleton variant="rounded" height={240} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label="Pages et leur référencement">
            <TableHead>
              <TableRow>
                <TableCell>Page</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Titre</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibles.map((page) => {
                const titre = etatTitre(page.metaTitle);
                const description = etatDescription(page.metaDescription);
                const choisie = selection?.ressource === page.ressource && selection.id === page.id;

                return (
                  <TableRow
                    key={`${page.ressource}-${page.id}`}
                    hover
                    selected={choisie}
                    onClick={() => ouvrir(page)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: choisie ? 700 : 400 }}>
                        {page.titre}
                        {!page.publiee && (
                          <Chip size="small" label="Brouillon" variant="outlined" sx={{ ml: 1 }} />
                        )}
                        {duplique(page) && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Titre en double"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {page.chemin}
                      </Typography>
                    </TableCell>
                    <TableCell>{LIBELLES_RESSOURCE[page.ressource]}</TableCell>
                    <TableCell>
                      <Pastille etat={titre.severite} valeur={page.metaTitle.trim().length} />
                    </TableCell>
                    <TableCell>
                      <Pastille
                        etat={description.severite}
                        valeur={page.metaDescription.trim().length}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selection !== null && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" component="h2">
              {selection.titre}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {LIBELLES_RESSOURCE[selection.ressource]} · {selection.chemin}
            </Typography>
          </Box>

          <ChampsSeo
            metaTitle={brouillon.metaTitle}
            metaDescription={brouillon.metaDescription}
            surTitre={(metaTitle) => {
              setBrouillon((actuel) => ({ ...actuel, metaTitle }));
              setSucces(false);
            }}
            surDescription={(metaDescription) => {
              setBrouillon((actuel) => ({ ...actuel, metaDescription }));
              setSucces(false);
            }}
            chemin={selection.chemin}
            erreurTitre={erreurChamp(erreur, "metaTitle")}
            erreurDescription={erreurChamp(erreur, "metaDescription")}
          />

          <PiedFormulaire
            modifie={modifie}
            enregistrement={mutation?.isPending ?? false}
            surEnregistrer={enregistrer}
            quoi="cette page"
            erreur={erreur}
            succes={succes}
          />
        </Paper>
      )}
    </Stack>
  );
}

function Pastille({
  etat,
  valeur,
}: {
  etat: "success" | "warning" | "error";
  valeur: number;
}) {
  return (
    <Chip
      size="small"
      color={etat === "success" ? "success" : etat}
      variant={etat === "success" ? "outlined" : "filled"}
      label={valeur === 0 ? "vide" : `${valeur} car.`}
    />
  );
}
